import { useRef } from "react";
import { MsgRedactedBase, type Msg } from "rujira.js";
import { privateIntent } from "../../redacted/v2/fees";

const snapshot = (message: Msg | null, scope?: string): string | undefined => {
  if (!scope || !(message instanceof MsgRedactedBase)) return;
  try {
    // Only execution fields, not price, balance or React object identity.
    return JSON.stringify([scope, message.account, privateIntent(message)]);
  } catch {
    // Unknown message types keep the existing identity-sensitive behavior.
    return;
  }
};

/** Prevent an identical private action from losing its reviewed quote on every
 * pool/balance update. This is render identity only, never fee authorization:
 * the session still validates quote age, identity, intent and policy on send. */
export const useStablePrivateMessage = (
  message: Msg | null,
  scope?: string
) => {
  const previous = useRef<{ key: string; message: Msg }>();
  const key = snapshot(message, scope);
  if (key === undefined || !message) {
    previous.current = undefined;
    return message;
  }
  const saved = previous.current;
  if (
    saved?.key === key &&
    // Never retain an old message that a caller subsequently mutated.
    snapshot(saved.message, scope) === key
  )
    return saved.message;
  previous.current = { key, message };
  return message;
};
